import toast from 'react-hot-toast';
import { utils } from '@/utils';
import { apiCall } from '@/utils/apiCallInstance';
import { fileDownloadFromResponse } from '@/utils/fileDownloadFromResponse';
import { operationRequirement, type OperationContext } from '@/session/operation';
import type { RequestScope } from '@/session/scopedTransport';

const validate = (path: string, fileName: string) => {
  if (!path) {
    throw new Error('Path is required');
  }
  if (!fileName) {
    throw new Error('File name is required');
  }
};

export type RequestParams = {
  path: string;
  fileName: string;
};

// Downloads require their captured scope and operation because receiving a
// response is not publication: the file click must still be suppressed if
// the organization changed while the body streamed.
export const downloadFile = async (scope: RequestScope, { path, fileName }: RequestParams, operation: OperationContext): Promise<void> => {
  try {
    validate(path, fileName);

    if (typeof window === 'undefined') return;
    if (!operation.isScopeCurrent()) return;

    const uriEncodedPath = encodeURIComponent(path);
    const downloadUrl = `/download/${uriEncodedPath}?filename=${encodeURIComponent(fileName)}`;

    const response = await apiCall().get(downloadUrl, undefined, undefined, {
      organizationScope: scope.kind === 'organization' ? { kind: 'organization', organization: scope.organization } : { kind: 'none' },
      operationRequirement: operationRequirement(operation, 'scope'),
    });

    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);

    const result = await fileDownloadFromResponse(response, fileName, {
      canPublish: operation.isScopeCurrent,
    });
    if (!result.success && !result.canceled && operation.isScopeCurrent()) {
      toast.error('Error Downloading File');
    }
  } catch (error: unknown) {
    if (!operation.isScopeCurrent()) return;
    const result = utils.error.handleAxiosError(error);
    if (!utils.error.isCanceledResponse(result)) toast.error('Error Downloading File');
  }
};
